import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api, bytes, uptime, type PowerState } from "../api";

interface ServiceSummary {
  id: string;
  kind: string;
  display_name: string;
  external_id: string;
  node_name: string;
  status: string;
  location: string;
  cluster_name: string;
  power_state: PowerState;
  cpu_cores: number | null;
  memory_total_bytes: number | null;
  uptime_seconds: number | null;
}

interface Usage {
  used: { services: number; cpu_cores: number; memory_mb: number; disk_gb: number };
  limits: { services: number; cpu_cores: number; memory_mb: number; disk_gb: number };
}

const KIND_LABEL: Record<string, string> = {
  pve_qemu: "Virtual machine",
  pve_lxc: "Container",
  hestia_account: "Hosting",
};

export function State({ value }: { value: string }) {
  return <span className={`state state--${value}`}>{value}</span>;
}

export function Services() {
  const { data, isPending, error } = useQuery({
    queryKey: ["services"],
    queryFn: () => api.get<ServiceSummary[]>("/api/services"),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <p className="label">Your services</p>
          <h1>Everything you run with us</h1>
        </div>
        <Link to="/services/new" className="btn btn--primary" style={{ textDecoration: "none" }}>
          Order a server
        </Link>
      </div>

      <Allowance />

      {error && (
        <p className="notice notice--error" role="alert">
          Services could not be loaded. Refresh to try again.
        </p>
      )}

      {isPending && <p className="muted">Loading…</p>}

      {data && data.length === 0 && (
        <div className="empty">
          <h2>Nothing here yet</h2>
          <p>
            Order a server and it appears here in about a minute. If you already have one with
            us, redeem your claim code to attach it.
          </p>
          <Link
            to="/services/new"
            className="btn btn--primary"
            style={{ textDecoration: "none", display: "inline-block", marginTop: "1.25rem" }}
          >
            Order a server
          </Link>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="ledger">
          {data.map((service) => (
            <Link
              key={service.id}
              to={`/services/${service.id}`}
              className="row"
              style={
                {
                  "--state": `var(--${stateVar(service.power_state)})`,
                } as React.CSSProperties
              }
            >
              <span className="row__rail" aria-hidden="true" />
              <span className="row__body">
                <span className="row__name">{service.display_name}</span>
                <span className="row__meta">
                  <span>{KIND_LABEL[service.kind] ?? service.kind}</span>
                  <span>#{service.external_id}</span>
                  <span>{service.node_name || service.cluster_name}</span>
                  {service.cpu_cores != null && <span>{service.cpu_cores} vCPU</span>}
                  {service.memory_total_bytes != null && (
                    <span>{bytes(service.memory_total_bytes)}</span>
                  )}
                  {service.uptime_seconds != null && <span>up {uptime(service.uptime_seconds)}</span>}
                </span>
              </span>
              <span className="row__side">
                <State value={service.power_state} />
              </span>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

function stateVar(state: PowerState): string {
  if (state === "running") return "live";
  if (state === "stopped") return "halt";
  if (state === "suspended") return "warn";
  return "idle";
}

/* What this account is using against what it may use.
 *
 * Shown before an order is attempted, not after one is refused. A limit you
 * only discover by hitting it is indistinguishable from a fault, and the
 * customer's next move is a support ticket rather than a smaller order. */
function Allowance() {
  const { data } = useQuery({
    queryKey: ["usage"],
    queryFn: () => api.get<Usage>("/api/services/usage/summary"),
  });

  if (!data) return null;

  const rows = [
    { label: "Servers", used: data.used.services, cap: data.limits.services, fmt: (n: number) => String(n) },
    { label: "Processor", used: data.used.cpu_cores, cap: data.limits.cpu_cores, fmt: (n: number) => `${n} vCPU` },
    {
      label: "Memory",
      used: data.used.memory_mb,
      cap: data.limits.memory_mb,
      fmt: (n: number) => bytes(n * 1024 * 1024) ?? String(n),
    },
    { label: "Storage", used: data.used.disk_gb, cap: data.limits.disk_gb, fmt: (n: number) => `${n} GB` },
  ];

  return (
    <section className="allowance">
      {rows.map((row) => {
        const ratio = row.cap ? Math.min(row.used / row.cap, 1) : 0;
        const level = ratio >= 1 ? " meter--critical" : ratio > 0.8 ? " meter--high" : "";
        return (
          <div key={row.label} className="allowance__item">
            <p className="label">{row.label}</p>
            <p className="allowance__figure mono">
              {row.fmt(row.used)} <span className="muted">of {row.fmt(row.cap)}</span>
            </p>
            <div className={`meter${level}`}>
              <i style={{ width: `${Math.max(ratio * 100, 1)}%` }} />
            </div>
          </div>
        );
      })}
    </section>
  );
}
